import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { PricingOptions } from "@/components/site/PricingOptions";
import { GuaranteeBadge } from "@/components/site/Guarantee";
import { FaqList } from "@/components/site/FaqList";
import { PaymentBadges } from "@/components/site/TrustBadges";
import { IMG } from "@/lib/content";

export const PricingSection = () => (
  <section id="pricing" data-testid="pricing-section" className="border-t border-line bg-[#080808]">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
      <Reveal className="mb-16 max-w-3xl">
        <p className="eyebrow">Choose how you start</p>
        <h2 className="font-display mt-5 text-4xl font-medium leading-tight tracking-tight text-white md:text-6xl">
          Less than one failed product test.
        </h2>
        <p className="mt-6 text-zinc-400 md:text-lg">One payment, instant digital download. Pay with card, Shop Pay, Klarna or PayPal at the secure Shopify checkout.</p>
        <PaymentBadges className="mt-6" />
      </Reveal>
      <PricingOptions />
    </div>
  </section>
);

export const GuaranteeSection = () => (
  <section data-testid="guarantee-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-40">
    <div className="flex flex-col items-start gap-14 border border-line p-8 md:flex-row md:items-center md:p-16">
      <Reveal><GuaranteeBadge size={240} /></Reveal>
      <Reveal delay={0.1}>
        <p className="eyebrow">The 7-day guarantee</p>
        <h2 className="font-display mt-5 text-4xl font-medium leading-tight tracking-tight text-white md:text-6xl">
          Try it for 7 days. <span className="font-serif-i text-zinc-400">Or get every franc back.</span>
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg">
          Read it, work through it, test it. If it is not right for you within 7 days, send one email and you get a full refund. The risk is on us, not on you.
        </p>
      </Reveal>
    </div>
  </section>
);

export const FounderSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} data-testid="founder-section" className="border-t border-line">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 md:px-12 md:py-36 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden border border-line">
            <motion.img src={IMG.founderWide} alt="Dilara" loading="lazy" style={{ y, scale: 1.18 }} className="h-full w-full object-cover object-top grayscale-[60%]" />
          </div>
        </Reveal>
        <div className="lg:col-span-7 lg:pl-8">
          <p className="eyebrow">Who is behind this</p>
          <h2 className="font-display mt-6 text-4xl font-medium leading-[1.05] tracking-tight text-white md:text-6xl">
            <MaskedLines inView lines={["I built this as", "a single mother.", <span key="l" className="font-serif-i text-zinc-400">No safety net.</span>]} />
          </h2>
          <Reveal delay={0.1}>
            <div className="mt-10 max-w-xl space-y-5 text-base leading-relaxed text-zinc-300 md:text-lg">
              <p>Between school runs and bedtime, I did not have time for trial and error. I needed a way to choose the right idea before investing money and months.</p>
              <p>The vision behind First Mover OS is bigger than one product: one day I want to build in biotech. This method is the system I use on that path, and I wrote it down so you can use it too.</p>
            </div>
            <Link to="/about" data-testid="founder-story-link" className="link-u mt-10 inline-block font-display text-white">Read my full story &rarr;</Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const FaqSection = () => (
  <section data-testid="faq-section" className="border-t border-line bg-[#080808]">
    <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 md:px-12 md:py-36 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <p className="eyebrow">Questions</p>
        <h2 className="font-display mt-5 text-4xl font-medium tracking-tight text-white md:text-5xl">Honest answers.</h2>
        <Link to="/support" className="link-u mt-6 inline-block text-zinc-300">All questions &amp; contact &rarr;</Link>
      </Reveal>
      <div className="lg:col-span-8"><FaqList /></div>
    </div>
  </section>
);
